import React from 'react';
import { X, Camera, Edit, Trash2, Eye, EyeOff, Tag, Package, Layers } from 'lucide-react';
import SafeImage from '../../SafeImage';
import ColorsInput from './ColorsInput';
import ImageColorsInput from './ImageColorsInput';
import { parseProductImages } from '../../../hooks/admin/useInventory';

export const AddProductModal = ({ inventory, categories }) => {
  const {
    showAddProduct, closeAddProduct, loadingProducts,
    productForm, setProductForm, handleCreateProduct,
    colorRows, addColorRow, removeColorRow, updateColorRow,
    justCreatedProduct, imageColorRows, updateImageColorRow, finishAddProductFlow,
  } = inventory;

  if (!showAddProduct) return null;

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm flex items-center justify-center z-[90] p-4">
      <div className="bg-surface rounded-md shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-line sticky top-0 bg-surface">
          <h3 className="font-display text-xl text-ink">{justCreatedProduct ? 'Add Photo Colors' : 'Add Product'}</h3>
          <button
            onClick={() => (justCreatedProduct ? finishAddProductFlow(false) : closeAddProduct())}
            className="p-2 text-ink-faint hover:text-ink hover:bg-paper rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {justCreatedProduct ? (
          <div className="p-6 space-y-5">
            <p className="text-sm text-ink-soft">
              "{justCreatedProduct.name}" was created. Optionally assign a color to each photo below — or skip this and add colors later from Edit Product.
            </p>
            <ImageColorsInput rows={imageColorRows} onUpdate={updateImageColorRow} />
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => finishAddProductFlow(false)}
                className="flex-1 border border-ink-faint text-ink py-3.5 rounded-full hover:bg-paper transition-colors dpc-label"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={() => finishAddProductFlow(true)}
                className="flex-1 bg-accent text-paper py-3.5 rounded-full hover:bg-accent-deep transition-colors dpc-label"
              >
                Save & Finish
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleCreateProduct} className="p-6 space-y-5">
            <div>
              <label className="dpc-label text-ink-soft block mb-1.5">Name *</label>
              <input
                type="text"
                required
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                className="w-full border border-ink-faint rounded-sm px-3.5 py-3 text-base bg-surface focus:ring-1 focus:ring-accent focus:border-accent"
                placeholder="e.g. Nairobi Tote — Cognac Leather"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="dpc-label text-ink-soft block mb-1.5">Price (KSh) *</label>
                <input
                  type="number"
                  required
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                  className="w-full border border-ink-faint rounded-sm px-3.5 py-3 text-base bg-surface focus:ring-1 focus:ring-accent focus:border-accent"
                  placeholder="4500"
                />
              </div>
              <div>
                <label className="dpc-label text-ink-soft block mb-1.5">In Stock *</label>
                <input
                  type="number"
                  required
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={productForm.stockQuantity}
                  onChange={(e) => setProductForm({ ...productForm, stockQuantity: e.target.value })}
                  className="w-full border border-ink-faint rounded-sm px-3.5 py-3 text-base bg-surface focus:ring-1 focus:ring-accent focus:border-accent"
                  placeholder="10"
                />
              </div>
            </div>

            <div>
              <label className="dpc-label text-ink-soft block mb-1.5">Category *</label>
              <select
                required
                value={productForm.categoryId}
                onChange={(e) => {
                  const cat = categories.find((c) => c.id === e.target.value);
                  setProductForm({ ...productForm, categoryId: e.target.value, category: cat ? cat.name : '' });
                }}
                className="w-full border border-ink-faint rounded-sm px-3.5 py-3 text-base bg-surface focus:ring-1 focus:ring-accent focus:border-accent"
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="dpc-label text-ink-soft block mb-1.5">Photo *</label>
              <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-ink-faint rounded-sm py-8 cursor-pointer hover:border-accent transition-colors bg-paper">
                {productForm.mainImage ? (
                  <span className="text-sm text-ink font-medium px-4 text-center break-all">{productForm.mainImage.name}</span>
                ) : (
                  <>
                    <Camera className="w-7 h-7 text-ink-faint" />
                    <span className="text-sm text-ink-soft">Take a photo or choose from gallery</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  required={!productForm.mainImage}
                  onChange={(e) => setProductForm({ ...productForm, mainImage: e.target.files[0] || null })}
                  className="hidden"
                />
              </label>
            </div>

            <div>
              <label className="dpc-label text-ink-soft block mb-1.5">More photos (optional)</label>
              <label className="flex items-center justify-center gap-2 border border-ink-faint rounded-sm py-3 cursor-pointer hover:border-accent transition-colors bg-surface">
                <Camera className="w-4 h-4 text-ink-faint" />
                <span className="text-sm text-ink-soft">
                  {productForm.additionalImages.length > 0 ? `${productForm.additionalImages.length} photo(s) added` : 'Add more photos'}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setProductForm({ ...productForm, additionalImages: Array.from(e.target.files) })}
                  className="hidden"
                />
              </label>
            </div>

            <div>
              <label className="dpc-label text-ink-soft block mb-1.5">Description (optional)</label>
              <textarea
                rows={3}
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                className="w-full border border-ink-faint rounded-sm px-3.5 py-3 text-base bg-surface focus:ring-1 focus:ring-accent focus:border-accent resize-none"
                placeholder="What makes this piece worth carrying?"
              />
            </div>

            <ColorsInput rows={colorRows} onAdd={addColorRow} onRemove={removeColorRow} onUpdate={updateColorRow} />

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={productForm.isFeatured}
                onChange={(e) => setProductForm({ ...productForm, isFeatured: e.target.checked })}
                className="w-5 h-5 rounded border-ink-faint text-accent focus:ring-accent"
              />
              <span className="text-sm text-ink-soft">Feature this product on the homepage</span>
            </label>

            <button
              type="submit"
              disabled={loadingProducts}
              className="w-full bg-accent text-paper py-3.5 rounded-full hover:bg-accent-deep transition-colors dpc-label disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingProducts ? 'Adding…' : 'Add Product'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export const EditProductModal = ({ inventory }) => {
  const {
    showEditProduct, editingProduct, closeEditProduct, loadingProducts,
    productForm, setProductForm, handleUpdateProduct,
    handleMainImageUpload, handleAdditionalImagesUpload, removeAdditionalImage,
    removedImageUrls, removeCurrentImage,
    colorRows, addColorRow, removeColorRow, updateColorRow,
    imageColorRows, updateImageColorRow,
  } = inventory;

  if (!showEditProduct || !editingProduct) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-800">Edit Product</h3>
            <button onClick={closeEditProduct} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleUpdateProduct} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                  <input
                    type="text"
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    placeholder="Enter product name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SKU *</label>
                  <input
                    type="text"
                    value={productForm.sku}
                    onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    placeholder="Enter SKU"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={productForm.description}
                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    placeholder="Enter product description"
                  />
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Pricing</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (KSh) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Original Price (KSh)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={productForm.originalPrice}
                    onChange={(e) => setProductForm({ ...productForm, originalPrice: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Inventory */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Inventory</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock Quantity *</label>
                  <input
                    type="number"
                    value={productForm.stockQuantity}
                    onChange={(e) => setProductForm({ ...productForm, stockQuantity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    placeholder="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={productForm.weight}
                    onChange={(e) => setProductForm({ ...productForm, weight: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dimensions (L×W×H cm)</label>
                  <input
                    type="text"
                    value={productForm.dimensions}
                    onChange={(e) => setProductForm({ ...productForm, dimensions: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    placeholder="e.g., 30×20×10"
                  />
                </div>
              </div>
            </div>

            {/* Category & Brand */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Category & Brand</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={productForm.category}
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="tote-bags">Tote Bags</option>
                    <option value="shoulder-bags">Shoulder Bags</option>
                    <option value="crossbody-bags">Crossbody Bags</option>
                    <option value="clutches">Clutches</option>
                    <option value="satchels">Satchels</option>
                    <option value="backpacks">Backpacks</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                  <input
                    type="text"
                    value={productForm.brand}
                    onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    placeholder="Enter brand name"
                  />
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Colors</h4>
              <ColorsInput rows={colorRows} onAdd={addColorRow} onRemove={removeColorRow} onUpdate={updateColorRow} />
            </div>

            {/* Images */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Images</h4>
              <div className="space-y-4">
                {/* Current Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Images</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    {editingProduct.image && !removedImageUrls.includes(editingProduct.image) && (
                      <div className="relative">
                        <SafeImage src={editingProduct.image} alt="Main product" className="h-24 w-24 object-cover rounded-lg border-2 border-blue-300" />
                        <div className="absolute -top-2 -left-2 bg-blue-500 text-white rounded-full h-6 w-6 flex items-center justify-center text-xs">M</div>
                        <button
                          type="button"
                          onClick={() => removeCurrentImage(editingProduct.image)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center text-xs hover:bg-red-600"
                          aria-label="Remove main image"
                        >
                          ×
                        </button>
                        <p className="text-xs text-gray-600 mt-1 text-center">Main Image</p>
                      </div>
                    )}
                    {parseProductImages(editingProduct).map((image, index) => (
                      !removedImageUrls.includes(image) && (
                        <div key={index} className="relative">
                          <SafeImage src={image} alt={`Additional ${index + 1}`} className="h-24 w-24 object-cover rounded-lg border-2 border-gray-300" />
                          <div className="absolute -top-2 -left-2 bg-gray-500 text-white rounded-full h-6 w-6 flex items-center justify-center text-xs">{index + 1}</div>
                          <button
                            type="button"
                            onClick={() => removeCurrentImage(image)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center text-xs hover:bg-red-600"
                            aria-label={`Remove image ${index + 1}`}
                          >
                            ×
                          </button>
                          <p className="text-xs text-gray-600 mt-1 text-center">Image {index + 1}</p>
                        </div>
                      )
                    ))}
                  </div>
                </div>

                {/* New Main Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload New Main Product Image</label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-[#d44100] transition-colors">
                    <div className="space-y-1 text-center">
                      {productForm.mainImage ? (
                        <div className="flex flex-col items-center">
                          <img src={URL.createObjectURL(productForm.mainImage)} alt="New main product" className="h-32 w-32 object-cover rounded-lg" />
                          <p className="text-sm text-gray-600 mt-2">{productForm.mainImage.name}</p>
                          <button type="button" onClick={() => setProductForm({ ...productForm, mainImage: null })} className="text-red-600 hover:text-red-700 text-sm">
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="flex text-sm text-gray-600">
                          <label htmlFor="edit-main-image" className="relative cursor-pointer bg-white rounded-md font-medium text-[#d44100] hover:text-[#b83700] focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#d44100]">
                            <span>Upload new main image</span>
                            <input id="edit-main-image" name="edit-main-image" type="file" className="sr-only" accept="image/*" onChange={handleMainImageUpload} />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                      )}
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                    </div>
                  </div>
                </div>

                {/* New Additional Images Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload New Additional Images (Up to 10)</label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-[#d44100] transition-colors">
                    <div className="space-y-1 text-center">
                      <div className="flex text-sm text-gray-600">
                        <label htmlFor="edit-additional-images" className="relative cursor-pointer bg-white rounded-md font-medium text-[#d44100] hover:text-[#b83700] focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#d44100]">
                          <span>Upload new additional images</span>
                          <input id="edit-additional-images" name="edit-additional-images" type="file" className="sr-only" accept="image/*" multiple onChange={handleAdditionalImagesUpload} />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB each</p>
                      <p className="text-xs text-gray-500">({productForm.additionalImages.length}/10 images selected)</p>
                    </div>
                  </div>

                  {productForm.additionalImages.length > 0 && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                      {productForm.additionalImages.map((image, index) => (
                        <div key={index} className="relative">
                          <img src={URL.createObjectURL(image)} alt={`New additional ${index + 1}`} className="h-24 w-24 object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => removeAdditionalImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center text-xs hover:bg-red-600"
                          >
                            ×
                          </button>
                          <p className="text-xs text-gray-600 mt-1 truncate">{image.name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Photo Colors (per-image, higher fidelity than the plain Colors list above) */}
            {imageColorRows.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Photo Colors</h4>
                <ImageColorsInput rows={imageColorRows} onUpdate={updateImageColorRow} />
              </div>
            )}

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t">
              <button type="button" onClick={closeEditProduct} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" className="px-6 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700]" disabled={loadingProducts}>
                {loadingProducts ? 'Updating...' : 'Update Product'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Read-only product summary — opened when an admin clicks a product in the
// list, so they see the full picture before deciding what to do next
// (Edit / Activate-Deactivate / Delete) instead of landing straight in the
// Edit form.
export const ViewProductModal = ({ inventory }) => {
  const { showViewProduct, viewingProduct, closeViewProduct, viewToEdit, viewDelete, viewToggleActive } = inventory;
  const [activeImage, setActiveImage] = React.useState(0);

  React.useEffect(() => { setActiveImage(0); }, [viewingProduct?.id]);

  if (!showViewProduct || !viewingProduct) return null;

  const product = viewingProduct;
  const gallery = [product.image, ...parseProductImages(product)].filter(Boolean);
  const colors = (() => {
    if (!product.colors) return [];
    try {
      const parsed = JSON.parse(product.colors);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  })();
  const onSale = product.originalPrice && Number(product.originalPrice) > Number(product.price);

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm flex items-center justify-center z-[90] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white/95 backdrop-blur-sm z-10">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${product.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                {product.isActive ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                {product.isActive ? 'Active' : 'Inactive'}
              </span>
              {product.isFeatured && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Featured</span>
              )}
              {onSale && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#d44100]/10 text-[#d44100]">On sale</span>
              )}
            </div>
            <h3 className="text-2xl font-bold text-gray-800">{product.name}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{product.category}{product.sku ? ` · SKU ${product.sku}` : ''}</p>
          </div>
          <button onClick={closeViewProduct} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Gallery */}
          <div>
            <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden mb-3">
              {gallery.length > 0 ? (
                <SafeImage src={gallery[activeImage] || gallery[0]} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <Package className="h-16 w-16" />
                </div>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`flex-shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 transition-colors ${activeImage === i ? 'border-[#d44100]' : 'border-transparent'}`}
                  >
                    <SafeImage src={img} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col">
            <div className="flex items-baseline gap-3 mb-5">
              <span className="text-3xl font-bold text-gray-800">KSh {Number(product.price || 0).toLocaleString()}</span>
              {onSale && (
                <span className="text-gray-400 line-through text-lg">KSh {Number(product.originalPrice).toLocaleString()}</span>
              )}
            </div>

            {product.description && (
              <p className="text-sm text-gray-600 leading-relaxed mb-5">{product.description}</p>
            )}

            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 mb-5 text-sm">
              <div className="flex items-center gap-1.5 text-gray-400"><Layers className="h-3.5 w-3.5" /> Stock</div>
              <dd className={`font-medium ${product.stockQuantity <= 5 ? 'text-red-600' : 'text-gray-800'}`}>{product.stockQuantity} units</dd>
              {product.brand && (<><div className="flex items-center gap-1.5 text-gray-400"><Tag className="h-3.5 w-3.5" /> Brand</div><dd className="text-gray-800 font-medium">{product.brand}</dd></>)}
              {product.material && (<><dt className="text-gray-400">Material</dt><dd className="text-gray-800 font-medium">{product.material}</dd></>)}
              {product.dimensions && (<><dt className="text-gray-400">Dimensions</dt><dd className="text-gray-800 font-medium">{product.dimensions}</dd></>)}
              {product.weight && (<><dt className="text-gray-400">Weight</dt><dd className="text-gray-800 font-medium">{product.weight} kg</dd></>)}
            </dl>

            {colors.length > 0 && (
              <div className="mb-5">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Colours</p>
                <div className="flex flex-wrap gap-2">
                  {colors.map((c, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 text-xs text-gray-700">
                      <span className="w-3 h-3 rounded-full border border-gray-300" style={{ background: c.code }} />
                      {c.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Decisions */}
            <div className="mt-auto pt-5 border-t border-gray-100 space-y-2.5">
              <button
                onClick={viewToEdit}
                className="w-full flex items-center justify-center gap-2 bg-[#d44100] text-white py-3 rounded-lg font-semibold hover:bg-[#b83700] transition-colors"
              >
                <Edit className="h-4 w-4" /> Edit Product
              </button>
              <div className="flex gap-2.5">
                <button
                  onClick={viewToggleActive}
                  className="flex-1 flex items-center justify-center gap-2 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  {product.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {product.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={viewDelete}
                  className="flex-1 flex items-center justify-center gap-2 border border-red-200 text-red-600 py-2.5 rounded-lg font-medium hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
